import React from 'react';
import { Rocket, Envelope, Sparkle, Calendar, Gift, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const LaunchPlanCard = ({ onAction, onClose }) => {
  const launchSteps = [
    {
      id: 'email_campaign',
      icon: Envelope,
      title: 'Write Email Campaign',
      description: 'Create a launch email sequence to announce your offer',
      action: 'email-campaign',
      color: 'from-blue-500 to-cyan-500',
      priority: 1
    },
    {
      id: 'content_promotion',
      icon: Sparkle,
      title: 'Create Content to Promote',
      description: 'Generate short-form content promoting your offer',
      action: 'generate-content',
      color: 'from-purple-500 to-pink-500',
      priority: 2
    },
    {
      id: 'weekly_plan',
      icon: Calendar,
      title: 'Plan Your Launch Week',
      description: 'Build a 7-day promotion plan for maximum impact',
      action: 'run-my-week',
      color: 'from-amber-500 to-orange-500',
      priority: 3
    },
    {
      id: 'lead_magnet',
      icon: Gift,
      title: 'Generate Lead Magnet',
      description: 'Create a free lead generator to build your list',
      action: 'viral-ideas',
      color: 'from-green-500 to-emerald-500',
      priority: 4
    }
  ];

  const handleStepClick = (action) => {
    if (onAction) {
      onAction(action);
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
          <Rocket size={20} className="text-white" weight="fill" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Your Launch Plan</h3>
          <p className="text-sm text-slate-400">Let's get your offer in front of people</p>
        </div>
      </div>

      {/* Launch Steps */}
      <div className="space-y-3">
        {launchSteps.map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.button
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleStepClick(step.action)}
              className="w-full p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl text-left transition-all group"
            >
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 bg-gradient-to-br ${step.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon size={20} className="text-white" weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-slate-500 text-xs font-medium">Step {index + 1}</span>
                  </div>
                  <h4 className="text-white font-medium">{step.title}</h4>
                  <p className="text-slate-400 text-sm truncate">{step.description}</p>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-white transition-colors flex-shrink-0" />
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Progress Indicator */}
      <div className="mt-4 p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg">
        <div className="flex items-center space-x-2 text-green-400 text-sm">
          <CheckCircle size={16} weight="fill" />
          <span>Your offer and sales page are ready. Now let's launch!</span>
        </div>
      </div>
    </motion.div>
  );
};

export default LaunchPlanCard;
